import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  DatePicker,
  Table,
  Typography,
  Space,
  Row,
  Col,
  Statistic,
  Button,
  Spin,
  message,
  Empty,
  Tag,
  Select
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { ReloadOutlined } from '@ant-design/icons';
import { api } from '../services/api';
import { useCurrency } from '../contexts/CurrencyContext';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const ITEM_TYPE_LABELS: Record<string, string> = {
  product: 'Parça',
  service: 'Hizmet',
  externalService: 'Dış Hizmet'
};

const ITEM_TYPE_COLORS: Record<string, string> = {
  product: 'blue',
  service: 'green',
  externalService: 'orange'
};

interface PersonelProfitRow {
  orderId: number;
  orderDate: string;
  lineNo: number;
  itemType: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  salesAmount: number;
  costAmount: number;
  profit: number;
}

interface PersonelProfitResponse {
  rows: PersonelProfitRow[];
  totals: {
    salesAmount: number;
    costAmount: number;
    profit: number;
  };
  startDate?: string;
  endDate?: string;
  technicianUserId?: number;
}

interface UserOption {
  id: number;
  firstName?: string;
  lastName?: string;
  username?: string;
}

const DEFAULT_RANGE_DAYS = 30;

const PersonelProfitReport: React.FC = () => {
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(DEFAULT_RANGE_DAYS, 'day').startOf('day'),
    dayjs().endOf('day')
  ]);
  const [userId, setUserId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PersonelProfitResponse | null>(null);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);

  const { baseCurrencySymbol } = useCurrency();

  const formatCurrency = useMemo(
    () =>
      (value: number) =>
        new Intl.NumberFormat('tr-TR', {
          style: 'currency',
          currency: 'TRY',
          currencyDisplay: 'symbol',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value ?? 0).replace('TRY', baseCurrencySymbol || '₺'),
    [baseCurrencySymbol]
  );

  const loadUsers = useCallback(async () => {
    try {
      setUsersLoading(true);
      const { data: list } = await api.get<UserOption[]>('/users');
      setUsers(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('Load users error:', e);
      message.error('Personel listesi yüklenemedi');
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const loadData = useCallback(
    async (range: [Dayjs, Dayjs], selectedUserId: number | null) => {
      if (!range || !range[0] || !range[1]) return;
      if (!selectedUserId || selectedUserId <= 0) {
        setData({
          rows: [],
          totals: { salesAmount: 0, costAmount: 0, profit: 0 },
          startDate: undefined,
          endDate: undefined,
          technicianUserId: undefined
        });
        return;
      }
      try {
        setLoading(true);
        const params: Record<string, string> = {
          startDate: range[0].startOf('day').format('YYYY-MM-DD'),
          endDate: range[1].endOf('day').format('YYYY-MM-DD'),
          userId: String(selectedUserId)
        };
        const { data: res } = await api.get<PersonelProfitResponse>(
          '/reports/personelprofit',
          { params }
        );
        setData(res);
      } catch (error: any) {
        console.error('Personel performans raporu load error:', error);
        message.error(
          error?.response?.data?.message || 'Personel performans raporu yüklenemedi'
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadData(dateRange, userId);
  }, [dateRange, userId, loadData]);

  const handleRangeChange = (values: null | [Dayjs | null, Dayjs | null]) => {
    if (!values || !values[0] || !values[1]) {
      setDateRange([
        dayjs().subtract(DEFAULT_RANGE_DAYS, 'day').startOf('day'),
        dayjs().endOf('day')
      ]);
      return;
    }
    setDateRange([values[0], values[1]]);
  };

  const runLoad = () => loadData(dateRange, userId);

  const columns: ColumnsType<PersonelProfitRow> = [
    {
      title: 'Fiş No',
      dataIndex: 'orderId',
      key: 'orderId',
      width: 90,
      render: (id: number) => `#${id}`
    },
    {
      title: 'Tarih',
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 150
    },
    {
      title: 'Satır',
      dataIndex: 'lineNo',
      key: 'lineNo',
      width: 70,
      align: 'right'
    },
    {
      title: 'Kalem Türü',
      dataIndex: 'itemType',
      key: 'itemType',
      width: 120,
      render: (type: string) => (
        <Tag color={ITEM_TYPE_COLORS[type] ?? 'default'}>
          {ITEM_TYPE_LABELS[type] ?? type}
        </Tag>
      )
    },
    {
      title: 'Kalem Adı',
      dataIndex: 'itemName',
      key: 'itemName',
      ellipsis: true
    },
    {
      title: 'Miktar',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
      width: 90,
      render: (value: number) =>
        typeof value === 'number' ? value.toFixed(2) : value
    },
    {
      title: 'Birim Fiyat',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      align: 'right',
      width: 110,
      render: (value: number) => formatCurrency(value ?? 0)
    },
    {
      title: 'Toplam (Satış)',
      dataIndex: 'salesAmount',
      key: 'salesAmount',
      align: 'right',
      width: 120,
      render: (value: number) => <Text>{formatCurrency(value)}</Text>
    },
    {
      title: 'Maliyet',
      dataIndex: 'costAmount',
      key: 'costAmount',
      align: 'right',
      width: 110,
      render: (value: number) => <Text>{formatCurrency(value)}</Text>
    },
    {
      title: 'Kâr',
      dataIndex: 'profit',
      key: 'profit',
      align: 'right',
      width: 120,
      render: (value: number) => (
        <Text type={value >= 0 ? 'success' : 'danger'}>
          {formatCurrency(value)}
        </Text>
      )
    }
  ];

  const totals = data?.totals;
  const personelLabel = userId
    ? users.find((u) => u.id === userId)
      ? `${users.find((u) => u.id === userId)?.firstName ?? ''} ${users.find((u) => u.id === userId)?.lastName ?? ''}`.trim() || `#${userId}`
      : `#${userId}`
    : '—';

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Title level={3}>Personel Performans Raporu</Title>
      <Text type="secondary">
        Seçili personelin seçili tarih aralığında verdiği hizmetler ve getirdiği gelir. Sadece <strong>teknisyen olarak atandığı</strong> satırlar listelenir; fiş tarihi (OrderDate) seçilen aralıkta olmalıdır.
      </Text>

      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Space wrap align="center">
            <RangePicker
              value={dateRange}
              onChange={handleRangeChange}
              allowClear={false}
              format="DD.MM.YYYY"
            />
            <Select
              placeholder="Personel seçin"
              style={{ minWidth: 220 }}
              value={userId}
              onChange={(v) => setUserId(v ?? null)}
              loading={usersLoading}
              allowClear
              showSearch
              optionFilterProp="label"
              options={users.map((u) => ({
                label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.username || `#${u.id}`,
                value: u.id
              }))}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={runLoad}
              loading={loading}
            >
              Yenile
            </Button>
          </Space>

          {userId && (
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>
                <Card bordered size="small">
                  <Statistic
                    title="Toplam Satış"
                    value={totals?.salesAmount ?? 0}
                    precision={2}
                    prefix={baseCurrencySymbol}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card bordered size="small">
                  <Statistic
                    title="Toplam Maliyet"
                    value={totals?.costAmount ?? 0}
                    precision={2}
                    prefix={baseCurrencySymbol}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card bordered size="small">
                  <Statistic
                    title="Toplam Kâr"
                    value={totals?.profit ?? 0}
                    precision={2}
                    prefix={baseCurrencySymbol}
                    valueStyle={{
                      color: (totals?.profit ?? 0) >= 0 ? '#3f8600' : '#cf1322'
                    }}
                  />
                </Card>
              </Col>
            </Row>
          )}
        </Space>
      </Card>

      <Card title={userId ? `Detay: ${personelLabel}` : 'Detay'}>
        <Spin spinning={loading}>
          {!userId ? (
            <Empty description="Raporu görmek için tarih aralığı ve personel seçin" />
          ) : data?.rows?.length ? (
            <Table<PersonelProfitRow>
              dataSource={data.rows}
              columns={columns}
              rowKey={(record) => `${record.orderId}-${record.lineNo}`}
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `Toplam ${t} kayıt` }}
              scroll={{ x: true }}
              summary={() =>
                totals ? (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={6}>
                      <Text strong>Toplam</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} colSpan={2} />
                    <Table.Summary.Cell index={2} align="right">
                      <Text strong>{formatCurrency(totals.salesAmount)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <Text strong>{formatCurrency(totals.costAmount)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                      <Text
                        strong
                        type={totals.profit >= 0 ? 'success' : 'danger'}
                      >
                        {formatCurrency(totals.profit)}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                ) : null
              }
            />
          ) : (
            <Empty description="Seçili personel ve tarih aralığı için kayıt bulunamadı" />
          )}
        </Spin>
      </Card>
    </Space>
  );
};

export default PersonelProfitReport;
